import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { API_BASE } from "../../lib/api";
import GoogleButton from "../../components/onboarding/GoogleButton";
import AuthBrandPanel from "../../components/onboarding/AuthBrandPanel";

const initial = { name: "", email: "", phone: "", password: "", confirmPassword: "" };

function validateField(key, values) {
  const v = values[key] || "";
  switch (key) {
    case "name":
      if (v.trim().length < 2) return "Please enter your full name.";
      return null;
    case "email":
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())) return "Please enter a valid email.";
      return null;
    case "phone":
      if ((v || "").length !== 10) return "Please enter a valid 10-digit mobile number.";
      return null;
    case "password":
      if ((v || "").length < 8) return "Password must be at least 8 characters.";
      return null;
    case "confirmPassword":
      if (v !== values.password) return "Passwords do not match.";
      return null;
    default:
      return null;
  }
}

export default function SignupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const setField = (k, val) => {
    setValues((s) => ({ ...s, [k]: val }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const onPhoneChange = (e) => {
    const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 10);
    setField("phone", digits);
  };

  const onBlur = (k) => () => {
    const err = validateField(k, values);
    setErrors((e) => ({ ...e, [k]: err || undefined }));
  };

  const validateAll = () => {
    const out = {};
    ["name", "email", "phone", "password", "confirmPassword"].forEach((k) => {
      const err = validateField(k, values);
      if (err) out[k] = err;
    });
    return out;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setBanner("");
    const errs = validateAll();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/onboarding/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: values.name.trim(),
          email: values.email.trim().toLowerCase(),
          phone: `+91${values.phone}`,
          password: values.password,
          referral_code: new URLSearchParams(location.search).get("ref") || undefined,
        }),
      });
      if (res.ok) {
        sessionStorage.setItem("onboarding_email", values.email.trim().toLowerCase());
        sessionStorage.setItem("onboarding_name", values.name.trim());
        navigate("/verify-email");
        return;
      }
      let detail = "";
      try { const j = await res.json(); detail = j?.detail || ""; } catch (_e) {}
      if (res.status === 409 && detail === "email_already_exists") {
        setErrors({ email: "An account with this email already exists. Sign in instead." });
      } else if (res.status === 409 && detail === "phone_already_registered") {
        setErrors({ phone: "This number is already registered." });
      } else {
        setBanner("Something went wrong. Please try again.");
      }
    } catch (_err) {
      setBanner("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "h-12 w-full rounded border border-ink-200 bg-white px-4 font-sans text-[15px] text-ink-950 placeholder:text-ink-400 shadow-sm outline-none transition-colors duration-150 focus:border-blue-800 focus:ring-4 focus:ring-blue-800/10";
  const labelCls = "mb-1.5 block font-sans text-[13px] font-medium text-ink-700";

  // Re-usable per-field input renderer keeps the JSX readable.
  // Defined here as a render function (not a component) to avoid React
  // unmounting the underlying <input> on every parent re-render, which
  // would otherwise drop focus and the typed character.
  const renderField = ({ id, type = "text", k, placeholder, autoComplete, onChange, value, className }) => (
    <input
      data-testid={`signup-${id}`}
      id={`signup-${id}`}
      type={type}
      value={value ?? values[k]}
      onChange={onChange || ((e) => setField(k, e.target.value))}
      onBlur={onBlur(k)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      className={className || inputCls}
    />
  );

  return (
    <div data-testid="signup-page" className="flex min-h-screen w-full flex-col overflow-hidden bg-white md:flex-row">
      <AuthBrandPanel testidPrefix="signup" />

      <main className="relative flex w-full items-center justify-center bg-white p-6 sm:p-12 md:w-[54%] lg:w-[50%] lg:p-16">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.15 }}
          className="w-full max-w-[420px]"
        >
          {/* Mobile logo (visible only when left pane is hidden) */}
          <div className="mb-8 flex justify-center md:hidden">
            <img src="/c-ore-logo.png" alt="C-ORE" draggable="false"
                 style={{ height: 38, width: 138, objectFit: "contain" }} />
          </div>

          {banner && (
            <motion.div
              initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
              data-testid="signup-banner"
              className="mb-4 flex items-start gap-2 rounded border border-status-critical-border bg-status-critical-bg p-3.5 font-sans text-[13px] text-status-critical-text"
            >
              {banner}
            </motion.div>
          )}

          <h2 className="font-display text-[28px] font-semibold tracking-tight text-ink-950">Create your account</h2>
          <p className="mt-1.5 font-sans text-[14px] text-ink-500">Start your 15-day free trial</p>

          <div className="mt-7">
            {/* Emergent-managed Google Sign-in. See /app/auth_testing.md. */}
            <GoogleButton testId="signup-google-button" label="Sign up with Google" />
          </div>

          <div className="my-6 flex items-center gap-3 font-sans text-[11.5px] uppercase tracking-widest text-ink-400">
            <span className="h-px flex-1 bg-ink-200" /> OR <span className="h-px flex-1 bg-ink-200" />
          </div>

          <form onSubmit={onSubmit} noValidate>
            {/* Field 1: Full Name */}
            <div className="mb-4">
              <label className={labelCls} htmlFor="signup-name">Full Name</label>
              {renderField({ id: "name", k: "name", placeholder: "Your full name", autoComplete: "name" })}
              {errors.name && <p data-testid="signup-error-name" className="mt-1.5 font-sans text-[12px] text-status-critical-text">{errors.name}</p>}
            </div>

            {/* Field 2: Email */}
            <div className="mb-4">
              <label className={labelCls} htmlFor="signup-email">Business Email</label>
              {renderField({ id: "email", type: "email", k: "email", placeholder: "you@yourcompany.com", autoComplete: "email" })}
              <p className="mt-1.5 font-sans text-[12px] text-ink-400">We will send your verification code here.</p>
              {errors.email && <p data-testid="signup-error-email" className="mt-1.5 font-sans text-[12px] text-status-critical-text">{errors.email}</p>}
            </div>

            {/* Field 3: Phone */}
            <div className="mb-4">
              <label className={labelCls} htmlFor="signup-phone">Mobile Number</label>
              <div className="flex">
                <span className="flex h-12 items-center rounded-l border border-r-0 border-ink-200 bg-ink-50 px-3 font-sans text-[13px] text-ink-500">
                  +91
                </span>
                <input
                  data-testid="signup-phone"
                  id="signup-phone"
                  type="tel"
                  inputMode="numeric"
                  value={values.phone}
                  onChange={onPhoneChange}
                  onBlur={onBlur("phone")}
                  placeholder="9876543210"
                  autoComplete="tel-national"
                  style={{ appearance: "textfield" }}
                  className={`${inputCls} flex-1 rounded-l-none`}
                />
              </div>
              {errors.phone && <p data-testid="signup-error-phone" className="mt-1.5 font-sans text-[12px] text-status-critical-text">{errors.phone}</p>}
            </div>

            {/* Field 4: Password */}
            <div className="mb-4">
              <label className={labelCls} htmlFor="signup-password">Password</label>
              <div className="relative">
                {renderField({
                  id: "password",
                  type: showPassword ? "text" : "password",
                  k: "password",
                  placeholder: "At least 8 characters",
                  autoComplete: "new-password",
                })}
                <button
                  data-testid="signup-toggle-password"
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-ink-400 transition-colors hover:text-blue-800"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && <p data-testid="signup-error-password" className="mt-1.5 font-sans text-[12px] text-status-critical-text">{errors.password}</p>}
            </div>

            {/* Field 5: Confirm Password */}
            <div className="mb-5">
              <label className={labelCls} htmlFor="signup-confirm">Confirm Password</label>
              <div className="relative">
                {renderField({
                  id: "confirm",
                  type: showConfirmPassword ? "text" : "password",
                  k: "confirmPassword",
                  placeholder: "Re-enter your password",
                  autoComplete: "new-password",
                })}
                <button
                  data-testid="signup-toggle-confirm"
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-ink-400 transition-colors hover:text-blue-800"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.confirmPassword && <p data-testid="signup-error-confirm" className="mt-1.5 font-sans text-[12px] text-status-critical-text">{errors.confirmPassword}</p>}
            </div>

            <motion.button
              data-testid="signup-submit"
              type="submit"
              disabled={loading}
              whileHover={!loading ? { y: -1 } : {}}
              whileTap={!loading ? { y: 0 } : {}}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-blue-800 font-sans text-[15px] font-semibold text-white shadow-[0_4px_14px_rgba(3,79,175,0.25)] transition-colors duration-150 hover:bg-blue-900 hover:shadow-[0_8px_20px_rgba(3,79,175,0.32)] disabled:cursor-default disabled:opacity-80"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? "Creating…" : "Create My Account"}
              {!loading && <ArrowRight size={15} />}
            </motion.button>

            <p className="mt-3 text-center font-sans text-[12px] text-ink-400">
              By signing up you agree to our Terms of Service.
            </p>
          </form>

          <div className="my-6 flex items-center gap-3 font-sans text-[11.5px] uppercase tracking-widest text-ink-400">
            <span className="h-px flex-1 bg-ink-200" /> OR <span className="h-px flex-1 bg-ink-200" />
          </div>

          <p className="text-center font-sans text-[13.5px] text-ink-500">
            Already have an account?{" "}
            <span
              data-testid="signup-go-signin"
              onClick={() => navigate("/login")}
              className="cursor-pointer font-semibold text-blue-800 hover:text-blue-900"
            >
              Sign in
            </span>
          </p>
        </motion.div>
      </main>
    </div>
  );
}
